import { ArrowDown, ArrowUp, Eye, EyeOff, Info, GripVertical, Trash2 } from "lucide-react";
import {
  cloneElement,
  isValidElement,
  useId,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { Tooltip } from "../ui/overlays";
import styles from "./content-editor-controls.module.css";

export function EditorField({
  label,
  children,
  full = false,
}: {
  label: string;
  children: ReactNode;
  full?: boolean;
}) {
  const generatedId = useId();
  const control = isValidElement(children) ? children as ReactElement<{ id?: string }> : null;
  const controlId = control?.props.id ?? generatedId;

  return (
    <div className={`ui-field ${full ? "full" : ""}`}>
      <label htmlFor={controlId}>{label}</label>
      {control ? cloneElement(control, { id: controlId }) : children}
    </div>
  );
}

export function SectionVisibilityControl({
  active,
  onChange,
  visibleTitle = "Bölüm sitede gösteriliyor",
  hiddenTitle = "Bölüm sitede gizli",
  visibleDescription = "Bu içerik yayınlandığında ziyaretçiler tarafından görülebilir.",
  hiddenDescription = "İçerik korunur ancak public sayfada gösterilmez.",
  ariaLabel = "Bölüm görünürlüğünü değiştir",
}: {
  active: boolean;
  onChange: (active: boolean) => void;
  visibleTitle?: string;
  hiddenTitle?: string;
  visibleDescription?: string;
  hiddenDescription?: string;
  ariaLabel?: string;
}) {
  const controlId = useId();
  return (
    <div className={styles.visibility} data-state={active ? "visible" : "hidden"}>
      <span className={styles.visibilityIcon} aria-hidden="true">
        {active ? <Eye size={17} /> : <EyeOff size={17} />}
      </span>
      <span className={styles.visibilityCopy}>
        <strong><label htmlFor={controlId}>{active ? visibleTitle : hiddenTitle}</label> <Tooltip label={active ? visibleDescription : hiddenDescription} side="top"><button type="button" className={styles.infoHelp} aria-label={active ? visibleDescription : hiddenDescription} onClick={event=>{event.preventDefault();event.stopPropagation();}}><Info size={14} aria-hidden="true"/></button></Tooltip></strong>
      </span>
      <label htmlFor={controlId} className={styles.visibilityControl}>
        <span>{active ? "Görünür" : "Gizli"}</span>
        <input id={controlId} type="checkbox" checked={active} onChange={(event) => onChange(event.target.checked)} aria-label={ariaLabel} />
        <span className={styles.visibilityTrack} aria-hidden="true"><i /></span>
      </label>
    </div>
  );
}

export function ItemVisibilityControl({
  active,
  onChange,
  className = "",
  ariaLabel = "Öğe görünürlüğünü değiştir",
}: {
  active: boolean;
  onChange: (active: boolean) => void;
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <label
      className={`${styles.itemVisibility} ${className}`.trim()}
      data-state={active ? "visible" : "hidden"}
      title={active ? "Public sayfada gösteriliyor" : "Public sayfada gizli"}
    >
      <input
        type="checkbox"
        checked={active}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={ariaLabel}
      />
      <span aria-hidden="true">{active ? <Eye size={15} /> : <EyeOff size={15} />}</span>
      <strong>{active ? "Görünür" : "Gizli"}</strong>
    </label>
  );
}

export function SortableHandle({
  label,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      className={`${styles.handle} ${className}`.trim()}
      aria-label={label}
      title="Sürükleyerek sırala"
      {...props}
    >
      <GripVertical size={16} />
    </button>
  );
}

export function SortableRowActions({
  itemLabel,
  index,
  total,
  onMove,
  onRemove,
  className = "",
  disabled = false,
}: {
  itemLabel: string;
  index: number;
  total: number;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div className={`${styles.actions} ${className}`.trim()}>
      <button type="button" onClick={() => onMove(-1)} disabled={disabled || index === 0} aria-label={`${itemLabel} yukarı taşı`} title="Yukarı taşı"><ArrowUp size={14} /></button>
      <button type="button" onClick={() => onMove(1)} disabled={disabled || index === total - 1} aria-label={`${itemLabel} aşağı taşı`} title="Aşağı taşı"><ArrowDown size={14} /></button>
      <button type="button" className={styles.remove} onClick={onRemove} disabled={disabled} aria-label={`${itemLabel} kaldır`}><Trash2 size={14} /><span>Kaldır</span></button>
    </div>
  );
}
